// Analytics and SEO tracking utilities

declare global {
  interface Window {
    gtag: (...args: any[]) => void
    dataLayer: any[]
  }
}

export class AnalyticsManager {
  private static instance: AnalyticsManager
  private googleAnalyticsId: string = ''
  private isInitialized: boolean = false

  static getInstance(): AnalyticsManager {
    if (!AnalyticsManager.instance) {
      AnalyticsManager.instance = new AnalyticsManager()
    }
    return AnalyticsManager.instance
  }

  // Initialize Google Analytics
  initGoogleAnalytics(measurementId: string) {
    this.googleAnalyticsId = measurementId
    
    // Create gtag script
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    document.head.appendChild(script)

    // Initialize dataLayer and gtag
    window.dataLayer = window.dataLayer || []
    window.gtag = function() {
      window.dataLayer.push(arguments)
    }
    
    window.gtag('js', new Date())
    window.gtag('config', measurementId, {
      // Enhanced ecommerce for retail tracking
      enhanced_ecommerce: true,
      // Track site search
      site_speed_sample_rate: 10
    })
    
    this.isInitialized = true
  }

  // Track page views
  trackPageView(pagePath: string, pageTitle: string) {
    if (!this.isInitialized) return
    
    window.gtag('config', this.googleAnalyticsId, {
      page_path: pagePath,
      page_title: pageTitle
    })
  }

  // Track site search (important for SEO)
  trackSiteSearch(searchTerm: string, category?: string, resultsCount?: number) {
    if (!this.isInitialized) return
    
    window.gtag('event', 'search', {
      search_term: searchTerm,
      search_category: category || 'products',
      search_results: resultsCount || 0
    })
  }

  // Track product views (for ecommerce)
  trackProductView(product: {
    item_id: string
    item_name: string
    category: string
    price: number
    currency: string
  }) {
    if (!this.isInitialized) return
    
    window.gtag('event', 'view_item', {
      currency: product.currency,
      value: product.price,
      items: [product]
    })
  }

  // Track add to cart
  trackAddToCart(product: {
    item_id: string
    item_name: string
    category: string
    quantity: number
    price: number
    currency: string
  }) {
    if (!this.isInitialized) return
    
    window.gtag('event', 'add_to_cart', {
      currency: product.currency,
      value: product.price * product.quantity,
      items: [product]
    })
  }

  // Track purchases
  trackPurchase(transactionData: {
    transaction_id: string
    value: number
    currency: string
    items: Array<{
      item_id: string
      item_name: string
      category: string
      quantity: number
      price: number
    }>
  }) {
    if (!this.isInitialized) return
    
    window.gtag('event', 'purchase', transactionData)
  }

  // Track custom events
  trackEvent(eventName: string, parameters: Record<string, any> = {}) {
    if (!this.isInitialized) return
    
    window.gtag('event', eventName, parameters)
  }
}

// Search Console verification
export function addSearchConsoleVerification(verificationCode: string) {
  const meta = document.createElement('meta')
  meta.name = 'google-site-verification'
  meta.content = verificationCode
  document.head.appendChild(meta)
}

// Bing Webmaster Tools verification
export function addBingVerification(verificationCode: string) {
  const meta = document.createElement('meta')
  meta.name = 'msvalidate.01'
  meta.content = verificationCode
  document.head.appendChild(meta)
}

// Initialize analytics for production
export function initAnalytics() {
  // Only initialize in production
  if (import.meta.env.MODE !== 'production') {
    return
  }

  const analytics = AnalyticsManager.getInstance()
  
  // Replace with your actual Google Analytics Measurement ID
  const GA_MEASUREMENT_ID = 'G-S72MX2JPK6' 
  
  if (GA_MEASUREMENT_ID !== 'G-S72MX2JPK6') {
    analytics.initGoogleAnalytics(GA_MEASUREMENT_ID)
  }

  // Add Search Console verification
   addSearchConsoleVerification('5-HEssYCtlKS30VBDL-OZiCp3CfIyrmGHR9fdtUdZhI')
  
  // Add Bing verification  
  // addBingVerification('your-bing-verification-code')
}

// Track Core Web Vitals for SEO
export function trackCoreWebVitals() {
  if ('PerformanceObserver' in window) {
    const analytics = AnalyticsManager.getInstance()
    
    // Track LCP (Largest Contentful Paint)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries()
      const lcpEntry = entries[entries.length - 1]
      
      analytics.trackEvent('core_web_vital', {
        metric_name: 'LCP',
        metric_value: Math.round(lcpEntry?.startTime || 0),
        metric_rating: (lcpEntry?.startTime || 0) < 2500 ? 'good' : (lcpEntry?.startTime || 0) < 4000 ? 'needs_improvement' : 'poor'
      })
    })
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true })

    // Track FID (First Input Delay)
    const fidObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const fid = (entry as any).processingStart - entry.startTime
        
        analytics.trackEvent('core_web_vital', {
          metric_name: 'FID', 
          metric_value: Math.round(fid),
          metric_rating: fid < 100 ? 'good' : fid < 300 ? 'needs_improvement' : 'poor'
        })
      }
    })
    fidObserver.observe({ type: 'first-input', buffered: true })

    // Track CLS (Cumulative Layout Shift)
    let clsScore = 0
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          clsScore += (entry as any).value
        }
      }
      
      analytics.trackEvent('core_web_vital', {
        metric_name: 'CLS',
        metric_value: Math.round(clsScore * 1000) / 1000,
        metric_rating: clsScore < 0.1 ? 'good' : clsScore < 0.25 ? 'needs_improvement' : 'poor'
      })
    })
    clsObserver.observe({ type: 'layout-shift', buffered: true })
  }
}

// Setup search tracking
export function setupSearchTracking() {
  const analytics = AnalyticsManager.getInstance()
  
  // Track internal site searches
  document.addEventListener('search', (event: any) => {
    if (event.detail?.searchTerm) {
      analytics.trackSiteSearch(
        event.detail.searchTerm,
        event.detail.category,
        event.detail.resultsCount
      )
    }
  })
}

// Export singleton instance
export const analytics = AnalyticsManager.getInstance()